import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Check, Close } from "@mui/icons-material";

const formatLabel = (val = "") =>
  String(val)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function InlineFieldCard({
  label,
  value,
  icon,
  iconBg,
  onSave,
  isPhone = false,
  isEmail = false,
  isSelect = false,
  options = [],
}) {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value ?? "");

  useEffect(() => {
    if (!editing) {
      setTemp(value ?? "");
    }
  }, [value, editing]);

  const handleChange = (val) => {
    let next = val;
    if (isPhone) {
      next = String(next).replace(/\D/g, "").slice(0, 10);
    }
    setTemp(next);
  };

  const handleCommit = () => {
    const trimmed = typeof temp === "string" ? temp.trim() : temp;
    onSave(trimmed);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setTemp(value ?? "");
  };

  const displayValue = isSelect ? formatLabel(value) : value;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 2.5,
        px: 2,
        minHeight: 110,
      }}
    >
      {/* Colored circular icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        {icon}
      </Box>

      {/* Label */}
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 500,
          color: "#9CA3AF",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      {/* Value with edit */}
      {editing ? (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TextField
            autoFocus
            size="small"
            select={isSelect}
            value={temp}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCommit();
              if (e.key === "Escape") handleCancel();
            }}
            inputProps={{
              inputMode: isPhone ? "numeric" : undefined,
            }}
            sx={{
              minWidth: 120,
              "& .MuiOutlinedInput-root": {
                fontSize: 13,
              },
              "& .MuiSelect-icon": isSelect ? {} : { display: "none" },
            }}
          >
            {isSelect &&
              options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {formatLabel(opt.label || opt.value)}
                </MenuItem>
              ))}
          </TextField>
          <IconButton
            size="small"
            onClick={handleCommit}
            sx={{
              width: 24,
              height: 24,
              bgcolor: "rgba(34, 197, 94, 0.1)",
              color: "#22C55E",
              "&:hover": { bgcolor: "rgba(34, 197, 94, 0.2)" },
            }}
          >
            <Check sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCancel}
            sx={{
              width: 24,
              height: 24,
              bgcolor: "rgba(239, 68, 68, 0.1)",
              color: "#EF4444",
              "&:hover": { bgcolor: "rgba(239, 68, 68, 0.2)" },
            }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => setEditing(true)}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: value ? "#1F2937" : "#9CA3AF",
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={displayValue || ""}
          >
            {displayValue || "-"}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            sx={{
              width: 18,
              height: 18,
              color: "#9CA3AF",
              "&:hover": { color: "#6B7280" },
            }}
          >
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}
